// booking/services/sentryBooking.ts
import * as Sentry from '@sentry/nextjs'

const ATTEMPT_KEY = 'booking_attempt_id'

export function getOrCreateBookingAttemptId(): string {
  if (typeof window === 'undefined') return 'server'
  const existing = sessionStorage.getItem(ATTEMPT_KEY)
  if (existing) return existing
  const id = crypto.randomUUID()
  sessionStorage.setItem(ATTEMPT_KEY, id)
  return id
}

export function setBookingTags(tags: {
  attemptId?: string
  cartId?: string
  stage?: number | string
  paymentMethod?: string
  locale?: string
  route?: string
}) {
  if (tags.attemptId) Sentry.setTag('booking_attempt_id', tags.attemptId)
  if (tags.cartId) Sentry.setTag('cart_id', tags.cartId)
  if (tags.stage != null) Sentry.setTag('booking_stage', String(tags.stage))
  if (tags.paymentMethod) Sentry.setTag('payment_method', tags.paymentMethod)
  if (tags.locale) Sentry.setTag('locale', tags.locale)
  if (tags.route) Sentry.setTag('route', tags.route)
}

export function setBookingContext(ctx: Record<string, unknown>) {
  // Keep this strictly non-PII
  Sentry.setContext('booking', ctx)
}

export function breadcrumb(
  message: string,
  data?: Record<string, unknown>,
  category = 'booking',
  level: Sentry.SeverityLevel = 'info',
) {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level,
  })
}

export function captureBookingMessage(
  code: string,
  extra?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'warning',
  fingerprint?: string[],
) {
  Sentry.withScope((scope) => {
    scope.setTag('booking_error_code', code)
    if (fingerprint) scope.setFingerprint(fingerprint)
    if (extra) scope.setExtras(extra)
    Sentry.captureMessage(code, level)
  })
}

export function captureBookingException(
  err: unknown,
  code: string,
  extra?: Record<string, unknown>,
  fingerprint?: string[],
) {
  Sentry.withScope((scope) => {
    scope.setTag('booking_error_code', code)
    if (fingerprint) scope.setFingerprint(fingerprint)
    if (extra) scope.setExtras(extra)
    Sentry.captureException(err)
  })
}

export function safeSnapshot(input: {
  stage?: number | null
  planSlug?: string | null
  numbers?: Record<string, unknown> | null
  sessionUuid?: string | null
  cartUuid?: string | null
  cartStatus?: string | null
  cartTotal?: number | null
}) {
  return {
    stage: input.stage ?? null,
    plan_slug: input.planSlug ?? null,
    numbers: input.numbers ?? null, // counts only, ok
    session_uuid: input.sessionUuid ?? null,
    cart_uuid: input.cartUuid ?? null,
    cart_status: input.cartStatus ?? null,
    cart_total: input.cartTotal ?? null,
  }
}
